/**
 * TenantSwitcher — Seletor de empresas (tenants) no header.
 *
 * CHANGELOG:
 *   RELEASE: 2026-08-28 v2.0.2 - Reduz altura fixa do ScrollArea para exibir
 *            exatamente 4 tenants no foco inicial (~208px); o restante aparece
 *            apenas via barra de rolagem interna do popover.
 *   RELEASE: 2026-08-28 v2.0.1 - Fixa altura do ScrollArea da lista de tenants em
 *            320px (h-[320px] ao invés de max-h-[320px]) para garantir scroll
 *            interno confiável e evitar que o popover cresça indefinidamente
 *            em listas grandes.
 *   RELEASE: 2026-07-24 v2.0.0 - Novo design intuitivo: substitui <Select> por
 *            Popover + Command (busca), exibe logo da empresa via Avatar,
 *            ícone de prédio com badge circular indicando quantidade total de
 *            empresas disponíveis (quando > 1), e check verde na empresa ativa.
 *            Referência: modelo de outro sistema aprovado pelo usuário.
 *   RELEASE: anterior v1.x - Select simples com dropdown básico.
 */
import { useState, useMemo } from 'react';
import { Building2, Check, ChevronDown, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';

// Tipo local — apenas os campos usados pelo switcher (mantém compatibilidade
// com o Tenant completo de useAuth passado pelo header).
interface Tenant {
  id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  logo_url?: string | null;
}

interface TenantSwitcherProps {
  availableTenants: Tenant[];
  currentTenant: Tenant | null;
  onSwitch: (tenantId: string) => void;
}

/**
 * Gera as iniciais do nome do tenant para o fallback do Avatar (máx 2 letras).
 */
function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Rótulo curto para descrição secundária (plano/status) na lista.
 */
function getTenantDescription(t: Tenant): string {
  const status = t.status && t.status !== 'active' ? ` · ${t.status}` : '';
  return `${t.plan || 'plano'}${status}`;
}

export function TenantSwitcher({
  availableTenants,
  currentTenant,
  onSwitch,
}: TenantSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  // Caso especial: usuário só tem 1 empresa — não faz sentido mostrar seletor.
  // Exibimos apenas logo/nome fixo, sem interação, para manter contexto visual.
  if (availableTenants.length <= 1) {
    const t = currentTenant;
    return (
      <div className="flex items-center gap-2">
        <Avatar className="h-7 w-7 border border-border/60">
          {t?.logo_url ? <AvatarImage src={t.logo_url} alt={t.name} /> : null}
          <AvatarFallback className="text-xs bg-muted text-muted-foreground">
            {t ? getInitials(t.name) : <Building2 className="h-3.5 w-3.5" />}
          </AvatarFallback>
        </Avatar>
        <span className="font-semibold text-foreground truncate max-w-[180px]">
          {t?.name || 'UaizyChat'}
        </span>
      </div>
    );
  }

  // Filtro de busca: nome ou slug (case-insensitive).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return availableTenants;
    return availableTenants.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.slug || '').toLowerCase().includes(q),
    );
  }, [availableTenants, query]);

  const totalCount = availableTenants.length;
  const active = currentTenant;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-9 gap-2 px-2 hover:bg-accent/60 focus-visible:ring-1"
          aria-label="Alternar empresa"
        >
          {/* Ícone de prédio com badge indicando total de empresas disponíveis.
              Serve como affordance visual clara de que há múltiplas empresas. */}
          <div className="relative">
            <Building2 className="h-5 w-5 text-muted-foreground" />
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-semibold leading-none flex items-center justify-center"
              aria-label={`${totalCount} empresas disponíveis`}
            >
              {totalCount > 9 ? '9+' : totalCount}
            </span>
          </div>

          {/* Logo + nome da empresa ativa */}
          <Avatar className="h-6 w-6 border border-border/60">
            {active?.logo_url ? (
              <AvatarImage src={active.logo_url} alt={active.name} />
            ) : null}
            <AvatarFallback className="text-[10px] bg-muted text-muted-foreground">
              {active ? getInitials(active.name) : '?'}
            </AvatarFallback>
          </Avatar>
          <span className="font-semibold text-foreground truncate max-w-[160px] text-sm">
            {active?.name || 'Selecionar empresa'}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground opacity-70" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        className="w-[320px] p-0 overflow-hidden"
      >
        {/* Barra de busca no topo do popover */}
        <div className="p-2 border-b border-border/60">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar empresa..."
              className="h-8 pl-7 text-sm"
              autoFocus
            />
          </div>
        </div>

        {/* Lista de empresas com scroll */}
        {/* v2.0.2: altura fixa em ~208px exibe 4 tenants no foco; restante via scroll interno */}
        <ScrollArea className="h-[208px]">
          <div className="py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                Nenhuma empresa encontrada
              </div>
            ) : (
              filtered.map((t) => {
                const isActive = t.id === active?.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      if (!isActive) onSwitch(t.id);
                      setOpen(false);
                      setQuery('');
                    }}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2 text-left transition-colors',
                      'hover:bg-accent/60 focus:bg-accent/60 focus:outline-none',
                      isActive && 'bg-accent/40',
                    )}
                  >
                    <Avatar className="h-9 w-9 border border-border/60 shrink-0">
                      {t.logo_url ? (
                        <AvatarImage src={t.logo_url} alt={t.name} />
                      ) : null}
                      <AvatarFallback className="text-xs bg-muted text-muted-foreground">
                        {getInitials(t.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-foreground truncate">
                        {t.name}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate capitalize">
                        {getTenantDescription(t)}
                      </div>
                    </div>
                    {isActive ? (
                      <Check className="h-4 w-4 text-emerald-600 shrink-0" />
                    ) : null}
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
